// The side menu's counters: each counted item reads what its screen reads, through the same query
// options, so the menu and the screen share one call and the menu adds no polling of its own. A
// read still loading, failed or refused shows no badge: the menu never shows 0 or an error.
import { useQuery } from '@tanstack/react-query';
import { Option, Result } from 'effect';
import { useOperatorCustomerId } from '../../deployment/admin-scope.tsx';
import { useOnboardingOverview } from '../../onboarding/queries/use-onboarding-query.ts';
import { dlqPageQueryOptions, NEWEST } from '../../operacoes/client/edge.ts';
import { dlqBadge, onboardingBadge } from './nav-counts.ts';
import type { NavBadge } from './nav-counts.ts';
import { useDriftBadge, useReceiverBadge } from './nav-profile-badges.ts';
import type { NavItem } from '../navigation.ts';

// The visible count is hidden from assistive technology; the full description joins the link's
// name instead, as in 'Drift regulatório, 3 endpoints com divergência'.
const Badge = ({ badge }: { readonly badge: Option.Option<NavBadge> }): JSX.Element | null =>
  Option.match(badge, {
    onNone: () => null,
    onSome: ({ text, tone, description }) => (
      <>
        <span className={`admin-nav-badge admin-nav-badge-${tone}`} aria-hidden="true">
          {text}
        </span>
        <span className="admin-visually-hidden">, {description}</span>
      </>
    ),
  });

const useOnboardingBadge = (): Option.Option<NavBadge> => {
  const query = useOnboardingOverview(useOperatorCustomerId());
  return query.isSuccess ? onboardingBadge(query.data) : Option.none();
};

const useDlqBadge = (): Option.Option<NavBadge> => {
  const query = useQuery(dlqPageQueryOptions(NEWEST));
  return query.status === 'success' && Result.isSuccess(query.data)
    ? dlqBadge(query.data.success)
    : Option.none();
};

const OnboardingCounter = (): JSX.Element => <Badge badge={useOnboardingBadge()} />;
const DriftCounter = (): JSX.Element => <Badge badge={useDriftBadge()} />;
const ReceiverCounter = (): JSX.Element => <Badge badge={useReceiverBadge()} />;
const DlqCounter = (): JSX.Element => <Badge badge={useDlqBadge()} />;

// Each counted item is its own component, so the hooks each one calls stay in a stable order.
const COUNTERS: Partial<Record<NavItem['id'], () => JSX.Element>> = {
  onboarding: OnboardingCounter,
  'drift-regulatorio': DriftCounter,
  receptora: ReceiverCounter,
  operacoes: DlqCounter,
};

/** The badge beside the menu item `id`: what waits on the operator there, if anything. */
export const NavCounter = ({ id }: { readonly id: NavItem['id'] }): JSX.Element | null => {
  const Counter = COUNTERS[id];
  if (Counter === undefined) return null;
  return <Counter />;
};
